import React, { useEffect, useMemo, useState } from "react";

import { Row } from "antd";
import { get } from "lodash";
import isEmpty from "lodash/isEmpty";
import moment from "moment/min/moment-with-locales.min";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CheckBox from "shared/components/CheckBox";
import Input from "shared/components/Input";
import Message from "shared/components/Message";
import NextPreviousFooter from "shared/components/NextPreviousFooter";
import Space from "shared/components/Space";
import { StepStore } from "shared/components/Tab";
import { NAME_SPACES } from "shared/locals/constants";
import { INPUT_ICON_STATES } from "utils/constants";
import { useApiTranslation } from "utils/hooks/useApiTranslation";

import FoldersTitle from "../Common/FoldersTitle";
import { INTERVENTION } from "../constants";

const getInputStatus = ({ inputChanged, inputValue, loading, error, data }) => {
  if (!inputValue || inputValue.length === 0) {
    return INPUT_ICON_STATES.SIMPLE;
  }
  if (loading) {
    return INPUT_ICON_STATES.LOAD;
  }
  if (error) {
    return INPUT_ICON_STATES.ERROR;
  }
  if (data) {
    if (inputChanged) {
      return INPUT_ICON_STATES.VERIFY;
    }
    return INPUT_ICON_STATES.CHECK;
  }
  return INPUT_ICON_STATES.VERIFY;
};

const Invoices = ({
  setActiveStep,
  step: { changeTab, onBack },
  queryProducts: {
    data: dataProducts,
    loading: loadingProducts,
    error: errorProducts,
  },
  queryProductRef: {
    data: dataProductRef,
    loading: loadingProductRef,
    called: calledProductRef,
  },
  queryRules: { data: dataRules },
  setProductError,
  getProducts,
  getProductByRef,
  onChange,
}) => {
  const { tApi } = useApiTranslation();
  const { t } = useTranslation(NAME_SPACES.NEW_FOLDER, {
    keyPrefix: "OTHER_PRODUCT",
  });
  const [sn1, setSn1] = useState("");
  const [sn2, setSn2] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [ref, setRef] = useState("");
  const [label, setLabel] = useState();
  const [disableRef, setDisableRef] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState();
  const [snChanged, setSnChanged] = useState(false);
  const [checked, setChecked] = useState(false);
  const purchaseDateRule = useMemo(
    () => get(dataRules, "rules.data.masks[0].purchaseDateInfo", {}),
    [dataRules],
  );

  const inputSNStatus = useMemo(
    () =>
      getInputStatus({
        inputChanged: snChanged,
        inputValue: sn1,
        loading: loadingProducts,
        error: errorProducts,
        data: dataProducts,
      }),
    [snChanged, sn1, loadingProducts, errorProducts, dataProducts],
  );
  const inputSN2Status = useMemo(
    () =>
      getInputStatus({
        inputChanged: snChanged,
        inputValue: sn2,
        loading: loadingProducts,
        error: errorProducts,
        data: dataProducts,
      }),
    [snChanged, sn2, loadingProducts, errorProducts, dataProducts],
  );
  const isEmptyRef = useMemo(
    () =>
      !loadingProductRef &&
      calledProductRef &&
      get(dataProductRef, "productByRef.data", []).length === 0,
    [dataProductRef, calledProductRef, loadingProductRef],
  );

  useEffect(() => {
    setSn1(get(dataProducts, "product.data[0].sn") || sn1);
    setSn2(get(dataProducts, "product.data[0].sn2") || sn2);
    setRef(get(dataProducts, "product.data[0].productRef") || ref);
    setSelectedProduct(get(dataProducts, "product.data[0]"));
    setDisableRef(!!get(dataProducts, "product.data[0].productRef"));
  }, [dataProducts]);

  const references = useMemo(() => {
    return get(dataProductRef, "productByRef.data", []).map(
      ({ productRef, productDesc, productCode }) => ({
        label: productRef ? `${productRef}-${productDesc}` : productDesc,
        value: `${productCode}-${productRef}-${productDesc}`,
      }),
    );
  }, [dataProductRef]);

  useEffect(() => {
    setRef("");
    setSelectedProduct();
    setDisableRef(false);
  }, [snChanged]);

  useEffect(() => {
    setProductError(false);
  }, [sn1, sn2]);

  useEffect(() => {
    setChecked(false);
    setLabel("");
  }, [isEmptyRef]);

  const onIconClick = () => {
    setSnChanged(false);
    if (inputSNStatus === INPUT_ICON_STATES.ERROR) {
      setSn1("");
      return;
    }
    setProductError();
    getProducts({
      variables: {
        where: {
          sn1,
        },
      },
    });
  };
  const onIcon2Click = () => {
    setSnChanged(false);
    if (inputSN2Status === INPUT_ICON_STATES.ERROR) {
      setSn2("");
      return;
    }
    setProductError();
    getProducts({
      variables: {
        where: {
          sn2,
        },
      },
    });
  };

  return (
    <>
      <div className={"newfolder--wrapper--content"}>
        <FoldersTitle title={t("TITLE")} subTitle={t("SUB_TITLE")} />
        <div className={"newfolder--wrapper--content--search"}>
          <div className={"newfolder--wrapper--content--search--input"}>
            <Message
              level={"error"}
              message={get(errorProducts, "messages[0].errorMesage")}
              visible={inputSNStatus === INPUT_ICON_STATES.ERROR}
            />
            <Message
              level={"warning"}
              message={t("WARNING_NOT_FOUND")}
              visible={ref && isEmptyRef}
            />
            <Space h={16} />
            <Input
              mode={"withStatus"}
              status={inputSNStatus}
              placeholder={tApi("SERIAL_1")}
              value={sn1}
              onChange={(e) => {
                setSn1(e.target.value);
                setSnChanged(true);
              }}
              onIconClick={onIconClick}
            />
            <Space h={16} />
            <Input
              placeholder={tApi("SERIAL_2")}
              value={sn2}
              mode={"withStatus"}
              status={inputSN2Status}
              onChange={(e) => {
                setSn2(e.target.value);
                setSnChanged(true);
              }}
              onIconClick={onIcon2Click}
            />
            <Space h={16} />
            {!isEmpty(purchaseDateRule) && (
              <>
                <Input
                  placeholder={`${t("PURCHASE_DATE")} ${
                    purchaseDateRule.isMandatory ? t("REQUIRED") : ""
                  }`}
                  selected={purchaseDate ? new Date(purchaseDate) : null}
                  mode={"date"}
                  status={""}
                  onChange={(date) => {
                    date && setPurchaseDate(moment(date).format("YYYY-MM-DD"));
                  }}
                />
                <Space h={16} />
              </>
            )}
            <Input
              mode="autoComplete"
              placeholder={t("REFERENCE")}
              options={references}
              value={ref}
              loading={loadingProductRef}
              disabled={disableRef}
              onSelect={(e) => {
                const _selectedProduct = get(
                  dataProductRef,
                  "productByRef.data",
                  [],
                ).find(
                  ({ productRef, productCode, productDesc }) =>
                    `${productCode}-${productRef}-${productDesc}` === e,
                );
                setRef(
                  _selectedProduct.productRef || _selectedProduct.productCode,
                );
                setSelectedProduct(_selectedProduct);
              }}
              onSearch={(e) => {
                setRef(e);
                setSelectedProduct();
                getProductByRef({
                  variables: {
                    where: {
                      ref: e,
                      exact: false,
                    },
                  },
                });
              }}
            />
            {selectedProduct && (
              <>
                <Space h={24} />
                <Input
                  placeholder={t("DESCRIPTION")}
                  value={
                    selectedProduct.productRef
                      ? `${selectedProduct.productRef}-${selectedProduct.productDesc}`
                      : selectedProduct.productDesc
                  }
                  disabled={true}
                />
              </>
            )}
            {ref && isEmptyRef && (
              <>
                <Space h={16} />
                <Row>
                  <CheckBox
                    label={t("USE_REFERENCE")}
                    onChange={() => setChecked((_checked) => !_checked)}
                    value={checked}
                  />
                </Row>
                <Space h={16} />
                <Input
                  placeholder={t("DESCRIPTION")}
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                  }}
                />
                <Space h={16} />
              </>
            )}
          </div>
        </div>
      </div>
      <NextPreviousFooter
        onPreviousClick={() => {
          onBack();
        }}
        previousEnabled={true}
        onNextClick={() => {
          onChange(
            {
              sn: sn1,
              sn2,
              isUnknownRef: checked,
              productRef: ref,
              productDesc: label,
              ...selectedProduct,
              purchaseDate,
            },
            true,
          );
          setActiveStep(2);
          changeTab(INTERVENTION);
        }}
        nextEnabled={
          !loadingProducts &&
          !loadingProductRef &&
          sn1 &&
          (!isEmpty(selectedProduct) ||
            (isEmpty(selectedProduct) && checked && label)) &&
          (isEmpty(purchaseDateRule) ||
            (!isEmpty(purchaseDateRule) &&
              purchaseDateRule.isMandatory &&
              purchaseDate) ||
            (!isEmpty(purchaseDateRule) && !purchaseDateRule.isMandatory))
        }
      />
    </>
  );
};

Invoices.propTypes = {
  queryRules: PropTypes.shape({
    data: PropTypes.shape({
      rules: PropTypes.shape({
        data: PropTypes.object,
      }),
    }),
  }).isRequired,
  queryProducts: PropTypes.shape({
    data: PropTypes.shape({
      product: PropTypes.shape({
        data: PropTypes.arrayOf(
          PropTypes.shape({
            sn: PropTypes.string,
            sn2: PropTypes.string,
            productRef: PropTypes.string,
          }),
        ),
      }),
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({
      message: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  queryProductRef: PropTypes.shape({
    data: PropTypes.shape({
      productByRef: PropTypes.shape({
        data: PropTypes.arrayOf(
          PropTypes.shape({
            productRef: PropTypes.string,
            productDesc: PropTypes.string,
            productBrand: PropTypes.string,
          }),
        ),
      }).isRequired,
    }).isRequired,
    loading: PropTypes.bool.isRequired,
    called: PropTypes.bool.isRequired,
  }).isRequired,
  setActiveStep: PropTypes.func.isRequired,
  getProducts: PropTypes.func.isRequired,
  getProductByRef: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  step: PropTypes.shape({
    changeTab: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
  }).isRequired,
  setProductError: PropTypes.func.isRequired,
};

Invoices.defaultProps = {};

export default StepStore(Invoices);
